import { SubscriptionDocumentRef, SupportingDocumentMeta } from './types';

/** `ArrayMaxSize(20)` on the attach route — exceeded, the whole PUT is rejected. */
export const MAX_SUPPORTING_DOCUMENTS = 20;

/** `@Length(1, 255)` on the route's `name`. */
const MAX_DOCUMENT_NAME_LENGTH = 255;

const RENDERED_CATEGORIES: SupportingDocumentMeta['category'][] = [
  'ProofOfAddress',
  'SourceOfIncomeOrWealth',
];

const isRenderedCategory = (category: string): category is SupportingDocumentMeta['category'] =>
  (RENDERED_CATEGORIES as string[]).includes(category);

/**
 * Both supporting-document categories are required before the wizard leaves
 * Sign Agreement, the step that hosts the uploader on both paths (spec
 * 2026-08-03). Acknowledging is what closes the window for good, so the
 * acknowledgment step re-checks this as well.
 */
export const hasRequiredSupportingDocuments = (
  documents: SupportingDocumentMeta[] | undefined,
): boolean =>
  !!documents &&
  documents.some((document) => document.category === 'ProofOfAddress') &&
  documents.some((document) => document.category === 'SourceOfIncomeOrWealth');

/**
 * Reshapes what the subscription reports into the shape the upload slots and
 * the gate above read.
 *
 * A category with no slot in this build is left out rather than shown under the
 * wrong heading — it stays in `subscriptionDocuments`, so a later replace-PUT
 * still carries it. `size` is 0 because the subscription stores no byte count
 * and a request per document just to label a chip is not worth making.
 */
export const toSupportingDocumentMeta = (
  documents: SubscriptionDocumentRef[],
): SupportingDocumentMeta[] =>
  documents
    .filter((document) => isRenderedCategory(document.category))
    .map((document) => ({
      id: document.fileId,
      category: document.category as SupportingDocumentMeta['category'],
      name: document.name,
      size: 0,
    }));

/**
 * The attach route replaces the whole set, so add and remove both produce the
 * complete list to send rather than a delta.
 *
 * Answers `null` when the list is already full: the route would reject the
 * twenty-first outright, and refusing here lets the caller say so without
 * spending an upload first.
 */
export const addSupportingDocument = (
  documents: SubscriptionDocumentRef[],
  document: SubscriptionDocumentRef,
): SubscriptionDocumentRef[] | null => {
  if (documents.length >= MAX_SUPPORTING_DOCUMENTS) return null;

  return [...documents, { ...document, name: document.name.slice(0, MAX_DOCUMENT_NAME_LENGTH) }];
};

export const removeSupportingDocument = (
  documents: SubscriptionDocumentRef[],
  fileId: string,
): SubscriptionDocumentRef[] => documents.filter((document) => document.fileId !== fileId);
